import React, { SyntheticEvent, useMemo } from "react";

import { faChartSimple } from "@fortawesome/pro-solid-svg-icons/faChartSimple";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";

import { EntityType } from "@mapmycustomers/shared/enum";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";
import { EntityTypeIcon } from "@mapmycustomers/ui";

import UnavailableMentionPill from "../UnavailableMentionPill";
import { useChartMentions } from "../util/ChartMentionsContext";
import {
  CHART_MENTION_MARKER,
  collapseChartMentionGaps,
  EntityMention,
  getUnresolvedMentionType,
  parseEntityMentions,
} from "../util/parseEntityMentions";
import useEntityMentionNavigation from "../util/useEntityMentionNavigation";

import styles from "./EntityMentionContent.module.scss";

export interface EntityMentionContentProps {
  allUsers: User[];
  content: string;
  mentions?: EntityMention[];
}

const EntityMentionContent = ({ allUsers, content, mentions }: EntityMentionContentProps) => {
  const { loading: chartsLoading, names: chartNames, onOpen: onOpenChart } = useChartMentions();
  const segments = useMemo(
    () => collapseChartMentionGaps(parseEntityMentions(content, mentions, chartNames)),
    [chartNames, content, mentions]
  );
  const navigate = useEntityMentionNavigation();
  const userById = new Map(allUsers.map((user) => [user.id, user]));

  return (
    <span>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          const unresolvedType = getUnresolvedMentionType(segment.text);
          if (!unresolvedType) {
            return <React.Fragment key={index}>{segment.text}</React.Fragment>;
          }
          if (unresolvedType === CHART_MENTION_MARKER && chartsLoading) {
            return null;
          }
          return <UnavailableMentionPill key={index} type={unresolvedType} />;
        }
        if (segment.type === "chart") {
          return (
            <Tooltip key={index} title={segment.displayName}>
              <span
                className={styles.chartPill}
                onClick={(event: SyntheticEvent<HTMLElement>) => {
                  // The pill sits inside a clickable row — a notification card
                  // being the case that bites — whose own handler would
                  // navigate elsewhere right after ours and win.
                  stopEvents(event);
                  onOpenChart?.(segment.chartId);
                }}
              >
                <FontAwesomeIcon className={styles.entityPillIcon} icon={faChartSimple} />
                <span className={styles.chartPillName}>{segment.displayName}</span>
                <FontAwesomeIcon className={styles.chartPillChevron} icon={faChevronRight} />
              </span>
            </Tooltip>
          );
        }
        const icon = (
          <EntityTypeIcon className={styles.entityPillIcon} entityType={segment.entityType} />
        );
        if (segment.entityType === EntityType.USER) {
          const user = userById.get(segment.entityId);
          const pill = (
            <span className={styles.entityPillStatic}>
              {icon}
              {segment.displayName}
            </span>
          );
          return (
            <Tooltip key={index} title={user ? userDisplayName(user) : undefined}>
              {pill}
            </Tooltip>
          );
        }
        return (
          <span
            className={styles.entityPill}
            key={index}
            onClick={(event: SyntheticEvent<HTMLElement>) => {
              // Same as the chart pill above: the row's own onClick would
              // otherwise swallow this and navigate somewhere else.
              stopEvents(event);
              navigate(segment.entityType, segment.entityId);
            }}
          >
            {icon}
            {segment.displayName}
          </span>
        );
      })}
    </span>
  );
};

export default EntityMentionContent;
